import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { news } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams();
  const item = news.find((n) => n.id === id);

  useSeo({
    title: item ? `${item.title} - 激情丛林电影资讯` : "资讯未找到 - 激情丛林电影",
    description: item ? item.excerpt : "未找到该资讯内容。",
    keywords: "激情丛林资讯,电影资讯,幕后花絮,官方公告",
  });

  if (!item) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-32 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-50">未找到这条资讯</h1>
        <p className="mt-2 text-sm text-foreground-400">它可能已被移除，或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = news
    .filter((n) => n.id !== item.id && n.category === item.category)
    .slice(0, 3);
  const fallback = news.filter((n) => n.id !== item.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallback;

  return (
    <article className="w-full">
      <div className="relative h-[300px] w-full overflow-hidden md:h-[400px]">
        <img
          src={item.cover}
          alt={`${item.title} 配图`}
          title={`${item.title} - 激情丛林电影资讯`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/70 to-background-950/30"></div>
      </div>

      <div className="mx-auto w-full max-w-3xl px-4 md:px-6">
        <div className="relative -mt-20">
          <Breadcrumb items={[{ label: "资讯", to: "/news" }, { label: "资讯详情" }]} />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-accent-500/40 bg-background-950/60 px-3 py-1 font-label text-xs text-accent-400 backdrop-blur">
              {item.category}
            </span>
            <span className="flex items-center gap-2 font-label text-xs text-foreground-400">
              <i className="ri-calendar-line"></i>
              {item.date}
            </span>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-50 md:text-3xl">
            {item.title}
          </h1>
        </div>

        <div className="mt-8 space-y-5 border-t border-background-800 pt-8 text-[15px] leading-8 text-foreground-300">
          {item.content.map((p) => (
            <p key={p.slice(0, 12)}>{p}</p>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-800 pt-6">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-5 py-2.5 font-label text-sm text-foreground-100 transition-colors hover:border-primary-500 hover:text-accent-400"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
          <span className="font-label text-xs text-foreground-500">
            本条资讯为演示内容，非真实公告
          </span>
        </div>
      </div>

      <section className="mx-auto w-full max-w-3xl px-4 py-14 md:px-6">
        <h2 className="mb-6 font-heading text-xl font-bold text-foreground-50">相关资讯推荐</h2>
        <div className="flex flex-col gap-4">
          {relatedList.map((n) => (
            <Link
              key={n.id}
              to={`/news/${n.id}`}
              className="panel group flex items-center gap-4 rounded-lg p-4 transition-colors hover:border-primary-500"
            >
              <span className="h-20 w-28 shrink-0 overflow-hidden rounded-md">
                <img
                  src={n.cover}
                  alt={`${n.title} 配图`}
                  title={`${n.title} - 激情丛林电影资讯`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-label text-xs text-accent-500">{n.category}</span>
                <span className="mt-1 block font-heading text-sm font-bold text-foreground-50 group-hover:text-accent-400 md:text-base">
                  {n.title}
                </span>
                <span className="mt-1 block font-label text-xs text-foreground-500">{n.date}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}